import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import toast from 'react-hot-toast';

const roleLinks = {
  citizen: [
    { to: '/citizen', label: 'Dashboard' },
    { to: '/citizen/map', label: 'Live Map' },
    { to: '/citizen/alerts', label: 'Alerts' },
  ],
  responder: [
    { to: '/responder', label: 'Missions' },
    { to: '/responder/map', label: 'Mission Map' },
  ],
  admin: [
    { to: '/admin', label: 'Command' },
    { to: '/admin/analytics', label: 'Analytics' },
    { to: '/admin/incidents', label: 'Incidents' },
  ],
};

export default function Navbar({ darkMode, setDarkMode }) {
  const { user, userProfile, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const role = userProfile?.role || 'citizen';
  const links = roleLinks[role] || [];

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch {
      toast.error('Logout failed');
    }
  };

  const toggleLang = (lang) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
    toast.success(lang === 'hi' ? 'Hindi enabled' : 'English enabled');
  };

  return (
    <nav className="sticky top-0 z-50 bg-navy-card border-b border-navy-border px-4 py-3 flex items-center justify-between">
      {/* Logo */}
      <Link to="/" className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-electric flex items-center justify-center shadow-glow-blue">
          <span className="text-white font-display font-bold text-sm">A</span>
        </div>
        <span className="font-display font-bold text-electric tracking-widest text-sm hidden sm:block">
          AEGISNET AI
        </span>
      </Link>

      {/* Desktop links */}
      <div className="hidden md:flex items-center gap-6">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`text-sm font-semibold tracking-wide transition-colors ${
              location.pathname === link.to
                ? 'text-electric'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {link.label}
          </Link>
        ))}
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-3">
        {/* Language toggle */}
        <div className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="text-xs font-mono text-gray-400 hover:text-white px-2 py-1 border border-navy-border rounded"
            aria-label="Change language"
          >
            {i18n.language === 'hi' ? 'हिं' : 'EN'}
          </button>
          <AnimatePresence>
            {langOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="absolute right-0 top-8 glass-card py-1 w-28 z-50"
              >
                {['en', 'hi'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => toggleLang(lang)}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-navy-border text-gray-300 hover:text-white"
                  >
                    {lang === 'en' ? '🇺🇸 English' : '🇮🇳 हिंदी'}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Dark mode toggle */}
        <button
          onClick={() => setDarkMode(!darkMode)}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-navy-border text-gray-400 hover:text-white hover:border-electric transition-colors"
          aria-label="Toggle dark mode"
        >
          {darkMode ? '☀️' : '🌙'}
        </button>

        {/* Role badge */}
        {userProfile && (
          <span
            className="hidden sm:inline text-xs font-mono px-2 py-1 rounded border"
            style={{
              color: role === 'admin' ? '#FF3B3B' : role === 'responder' ? '#FFB300' : '#00C896',
              borderColor: role === 'admin' ? '#FF3B3B' : role === 'responder' ? '#FFB300' : '#00C896',
              background: role === 'admin' ? 'rgba(255,59,59,0.1)' : role === 'responder' ? 'rgba(255,179,0,0.1)' : 'rgba(0,200,150,0.1)',
            }}
          >
            {role.toUpperCase()}
          </span>
        )}

        {/* Avatar + logout */}
        {user && (
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 group"
            title="Logout"
          >
            <div className="w-8 h-8 rounded-full bg-electric flex items-center justify-center text-xs font-bold">
              {(userProfile?.name || user.email || 'U')[0].toUpperCase()}
            </div>
          </button>
        )}

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <div className="space-y-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-5 h-0.5 bg-gray-400" />
            ))}
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute top-full left-0 right-0 bg-navy-card border-b border-navy-border z-50 overflow-hidden"
          >
            <div className="px-4 py-3 space-y-2">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-semibold text-gray-300 hover:text-white"
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="block py-2 text-sm font-semibold text-crimson"
              >
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
